import { useTranslation } from 'next-i18next'
import { useEffect, useRef, useState } from 'react'
import { v4 as uuid } from 'uuid'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import Equals from '~/bootcamp/Activities/components/Equals'
import Input from '~/bootcamp/Activities/components/Input'
import FractionsActivityWrapper from '~/bootcamp/Activities/Fractions/components/FractionsActivityWrapper'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import Divider from '~/bootcamp/Activities/components/Divider'
import useFocus from '~/bootcamp/Activities/hooks/useFocus'
import { hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import { generateRandomDenominatorAndAnswer } from './helpers'

const DENOMINATORS = [2, 4, 5, 10]
const MAX_HISTORY_LENGTH = 3

function UnitFractionDecimal({
  answerState,
  onNextQuestionClick,
  onAnswerStateChange,
  onInstanceChange,
  actionButtonsFC,
  autofocus = true,
}: ActivityProps): React.ReactElement {
  const { t } = useTranslation('generics')
  const [answer, setAnswer] = useState('')
  const [taskHistory, setTaskHistory] = useState<number[]>([])
  const [inputKey, setInputKey] = useState<string>(uuid())
  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)
  const [{ denominator, result }, setExpressionData] = useState({
    denominator: 1,
    result: '',
  })

  useEffect(() => {
    setAnswer('')
    const [_denominator, _answer] = generateRandomDenominatorAndAnswer(DENOMINATORS, taskHistory)
    setExpressionData({ denominator: _denominator, result: _answer })
  }, [taskHistory])

  // `result` is the answer; it only gates the placeholder first render.
  useEmitInstance(onInstanceChange, result ? `\\frac{1}{${denominator}} = \\square` : null)

  useEffect(() => {
    onAnswerStateChange(null)
  }, [answer, onAnswerStateChange])

  const handleOnCheckAnswerClick = (): void => {
    const isCorrect = Number(answer).toFixed(2) === result
    onAnswerStateChange(isCorrect)
  }

  const handleOnNextQuestionClick = (): void => {
    setTaskHistory((prevState) => {
      return [denominator, ...prevState].slice(0, MAX_HISTORY_LENGTH)
    })
    onAnswerStateChange(null)
    onNextQuestionClick()
    setInputKey(uuid())
  }

  const onEnterPress = (): void => {
    if (answer !== '') {
      hocOnEnterPress(answerState, handleOnCheckAnswerClick, handleOnNextQuestionClick)
    }
  }

  return (
    <ActivityWrapper>
      <FractionsActivityWrapper>
        <div className="w-6 flex flex-col items-center">
          <span>1</span>
          <Divider />
          <span>{denominator}</span>
        </div>
        <span className="block sm:inline-block">
          <Equals />
          <Input
            aria-label={t('ariaLabelButtons.insertDecimalValue')}
            autoFocus={autofocus}
            isError={answerState === false}
            key={inputKey}
            maxDigits={4}
            onChange={(value) => setAnswer(value)}
            onEnter={onEnterPress}
            ref={inputRef}
            value={answer}
          />
        </span>
      </FractionsActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid: answer !== '',
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleOnCheckAnswerClick,
          clearFormAction: () => setAnswer(''),
          goToNextQuestionAction: handleOnNextQuestionClick,
        })}
    </ActivityWrapper>
  )
}

export default UnitFractionDecimal
